import { FlagSolid, HomeSolid, MapPinSolid, PinSolid, Sparkles } from '@byway/icons';
import clsx from 'clsx';
import pluralize from 'pluralize';
import { Button } from 'react-aria-components';
import { useMap } from 'react-map-gl/maplibre';
import type { MapOverviewItem } from '~/components/Playground/mapHelpers';
import { Tooltip, TooltipTrigger } from '~/components/Tooltip';
import { usePlaygroundActions, usePlaygroundIsDragging, usePlaygroundUIState } from '~/stores/playgroundStore';
import { trackEvent } from '~/utils';
import styles from './MapMarkers.module.css';
import type { ClusterMarkerProps, MapMarkerProps } from './MapMarkers.types';

const getTooltipContentForRoute = (title: string, overviewItem?: MapOverviewItem) => {
  let subtitle: string | null = null;
  if (overviewItem?.type === 'Stay') {
    subtitle = pluralize('night', overviewItem.nights, true);
  } else if (overviewItem?.type === 'Stop') {
    subtitle = 'Stop';
  }

  return (
    <div className={styles.tooltipContent}>
      <h2 className={styles.tooltipHeading}>{title}</h2>
      {subtitle && <p className={styles.tooltipSubtext}>{subtitle}</p>}
    </div>
  );
};

function ClusterMarker({ pointCount, isUnreachable = false, clickHandler }: ClusterMarkerProps) {
  const isDragging = usePlaygroundIsDragging();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const button = (
    <Button
      className={clsx(styles.pinCircle, styles.centered, {
        [styles.unreachableBackground]: isUnreachable,
        [styles.reachableBackground]: !isUnreachable
      })}
      onPress={() => {
        if (isDragging) return;
        clickHandler();
      }}
    >
      <span className={styles.markerText}>{pointCount}</span>
    </Button>
  );

  if (isMobile) return button;

  return (
    <TooltipTrigger>
      {button}
      <Tooltip>
        {pointCount} places{isUnreachable ? ' out of reach' : ''}
      </Tooltip>
    </TooltipTrigger>
  );
}

function MapMarker({
  place,
  isAdded,
  isUnreachable = false,
  variant = 'default',
  isSelected = false,
  isSparkle = false,
  customZoomOffset,
  isReviewPage = false
}: MapMarkerProps) {
  const { current: map } = useMap();
  const { placeDialog, aiTravelPlannerResponse } = usePlaygroundUIState();
  const { setPlaceDialog, openDrawer } = usePlaygroundActions();
  const isDragging = usePlaygroundIsDragging();
  if (!place) return null;

  const isActiveDialog = place.id === (placeDialog?.id || false);

  const currentZoom = map?.getZoom() || 0;
  const targetZoom = 2;

  // detect device
  const isMobile = window.innerWidth < 768;
  const height = window.innerHeight;

  const mobileOffset = -1 * (height / 2 - 213 - 32);

  // Use custom zoom offset if provided, otherwise use default
  const desktopOffset = customZoomOffset || ([0, -190] as [number, number]);

  const options = {
    center: [place.location.longitude, place.location.latitude] as [number, number],
    offset: isMobile ? ([0, mobileOffset] as [number, number]) : desktopOffset,
    duration: 400,
    zoom: Math.max(currentZoom, targetZoom)
  };

  const onPress = () => {
    if (isDragging) return;

    // Don't open the card dialog for start/end markers - open origin/end panel instead
    if (variant === 'start' || variant === 'end') {
      openDrawer('start-end');
      return;
    }

    if (isSparkle) {
      setPlaceDialog({
        ...place
      });
      trackEvent('playground_ai_pin_click', {
        event_category: 'Playground',
        event_label: 'playground_ai_pin_click'
      });
    } else {
      setPlaceDialog(place || null);
      trackEvent('playground_map_pin_click', {
        event_category: 'Playground',
        event_label: 'playground_map_pin_click',
        place_id: place ? place.placeId : 'unknown'
      });
    }

    setTimeout(() => {
      map?.flyTo(options);
    }, 0);
  };

  const isEssence = false; // Temp disable essence pins - straightforward styles for now

  // Get icon based on marker type and variant
  const getIcon = () => {
    if (isSparkle) {
      return <Sparkles size="large" ariaLabel="Purple Sparkle icon" color="white" />;
    }

    switch (variant) {
      case 'start':
        return <HomeSolid color="white" size="small" ariaLabel="origin" />;
      case 'end':
        return <FlagSolid color="white" size="small" ariaLabel="destination" />;
      default:
        return isEssence ? (
          <PinSolid size="small" ariaLabel="places" color="white" />
        ) : (
          <MapPinSolid size="small" ariaLabel="places" color="white" />
        );
    }
  };

  // Get tooltip content
  const getTooltipContent = () => {
    if (isUnreachable) {
      return `This place is too far away to reach, given your filters. Lengthen your date range or remove selected places to access it.`;
    }

    return <h2 className={styles.tooltipHeading}>{place.placeName}</h2>;
  };

  const buttonClasses = clsx(styles.pinCircle, {
    // Sparkle-specific styles
    [styles.sparkleMarker]: isSparkle,
    [styles.selectedBackground]: isSparkle && isSelected,
    [styles.sparkleMarkerPulsate]: isSparkle && aiTravelPlannerResponse !== null,

    // Regular marker styles
    [styles.selectedBackground]: !isSparkle && (variant === 'start' || variant === 'end' || isAdded),
    [styles.unreachableBackground]: !isSparkle && isUnreachable,
    [styles.reachableBackground]: !isSparkle && !isUnreachable && !isAdded && variant === 'default',
    [styles.reviewBackground]: isReviewPage,
    [styles.active]: !isSparkle && isActiveDialog
  });

  const _button = (
    <Button onPress={onPress} className={buttonClasses}>
      {getIcon()}
    </Button>
  );

  return (
    <div className={`${styles.pinWrapper} ${isActiveDialog ? styles.active : ''}`}>
      <TooltipTrigger>
        <Button onPress={onPress} className={buttonClasses}>
          {getIcon()}
        </Button>
        <Tooltip>{getTooltipContent()}</Tooltip>
      </TooltipTrigger>
      <div className={styles.placeLabel}>{place.placeName}</div>
    </div>
  );
}

//// Pins below used on journey page maps, jg map - non-interactive pins without rich content data (JAI data only)
function MapNonInteractiveMarker({
  overviewItem,
  variant = 'default'
}: {
  overviewItem: MapOverviewItem;
  variant?: 'default' | 'start' | 'end';
}) {
  const isEssence = false; // marker.isEssencePlace ?? true // disable essence pins temporarily - simple styles for now

  const isStop = overviewItem.type === 'Stop';

  const getIconAndLabel = () => {
    switch (variant) {
      case 'start':
        return {
          icon: <HomeSolid color="white" size="small" ariaLabel="origin" />,
          ariaLabel: 'origin'
        };
      case 'end':
        return {
          icon: <FlagSolid color="white" size="small" ariaLabel="destination" />,
          ariaLabel: 'destination'
        };
      default:
        if (isStop) return { icon: null, ariaLabel: 'stop' };
        return {
          icon: <MapPinSolid color="white" size="small" ariaLabel="places" />,
          ariaLabel: 'places'
        };
    }
  };

  const { icon, ariaLabel } = getIconAndLabel();

  return (
    <div className={styles.pinWrapper}>
      <TooltipTrigger>
        <Button
          aria-label={ariaLabel}
          className={clsx(styles.pinCircle, {
            [styles.selectedBackground]: isEssence,
            [styles.nonEssenceBackground]: !isEssence && !isStop,
            [styles.stopBackground]: isStop,
            [styles.startEndBackground]: variant === 'start' || variant === 'end'
          })}
        >
          {icon}
        </Button>
        <Tooltip>{getTooltipContentForRoute(overviewItem.title, overviewItem)}</Tooltip>
      </TooltipTrigger>
      <div className={styles.placeLabel}>{overviewItem.title}</div>
    </div>
  );
}

export { ClusterMarker, MapNonInteractiveMarker as MapOverviewMarker, MapMarker };
